import type {
  AcudienteFormPayload,
  PuntoEntregaDetail,
  PuntoEntregaParticipanteFormPayload,
} from '@src/modules/puntos_entrega/domain/puntoEntregaModel.ts';
import { entregaNombreById, lugarNombreByRef } from '@src/modules/puntos_entrega/domain/puntoEntregaCatalogLabels.ts';
import type {
  RedencionBonoCatalogSnapshot,
  RedencionBonoListItem,
  RedencionBonoParticipanteFormPayload,
} from '@src/modules/redencion_bono/domain/redencionBonoModel.ts';
import type { OfflineSyncPuntosEntregaExportPort } from '../domain/ports/offlineSyncExportPorts.ts';
import type {
  OfflineSyncRedencionBundle,
  OfflineSyncWireAcudiente,
  OfflineSyncWirePuntoEntrega,
  OfflineSyncWirePuntoParticipante,
  OfflineSyncWireRedencionBundle,
  OfflineSyncWireRedencionHeader,
  OfflineSyncWireRedencionParticipante,
} from '../domain/offlineSyncPayload.ts';
import {
  barrioNombreById,
  comunaNombreById,
  participanteCatalogWire,
} from './offlineSyncWireCatalogHelpers.ts';

export function mapWireAcudiente(
  pe: OfflineSyncPuntosEntregaExportPort,
  a?: AcudienteFormPayload,
): OfflineSyncWireAcudiente | undefined {
  if (!a) return undefined;
  const parentescoNombre = pe.listParentesco().find((p) => p.id === a.parentescoId)?.nombre ?? '';
  return {
    nombre: a.nombre,
    documentoFrontal: a.documentoFrontal,
    documentoReverso: a.documentoReverso,
    documentoAutorizadoFrontal: a.documentoAutorizadoFrontal,
    documentoAutorizadoReverso: a.documentoAutorizadoReverso,
    autorizado: a.autorizado,
    parentescoNombre,
  };
}

export function mapWirePuntoParticipante(
  pe: OfflineSyncPuntosEntregaExportPort,
  catalog: RedencionBonoCatalogSnapshot,
  row: PuntoEntregaParticipanteFormPayload,
): OfflineSyncWirePuntoParticipante {
  const ids = participanteCatalogWire(pe, row.participanteId);
  return {
    publicId: ids.publicId,
    documento: ids.documento,
    nombre: row.nombreParticipante,
    telefono: row.telefono,
    celular: row.celular,
    presentaAcudienteAutorizado: row.presentaAcudienteAutorizado,
    acudiente: mapWireAcudiente(pe, row.acudiente),
    asistencia: row.asistencia,
    capacitado: row.capacitado,
    acta: row.acta,
    entregaBolsa: row.entregaBolsa,
    bono: row.bono,
    bonoPublicId: row.bonoId,
    tipoBonoPublicId: null,
    tipoBonoNombre: row.tipoBono.trim(),
    firmaBase64: row.firmaBase64,
    observaciones: row.observaciones,
    registroFotografico: row.registroFotografico,
    politicaTratamientos: row.politicaTratamientos,
  };
}

export function mapWirePuntoEntrega(
  pe: OfflineSyncPuntosEntregaExportPort,
  catalog: RedencionBonoCatalogSnapshot,
  row: PuntoEntregaDetail,
): OfflineSyncWirePuntoEntrega {
  return {
    publicId: row.publicId,
    entregaPublicId: row.entregaId,
    entregaNombre: entregaNombreById(pe, row.entregaId),
    esRemanente: row.esRemanente,
    lugarPublicId: row.lugarId,
    lugarNombre: lugarNombreByRef(pe, row.lugarId, row.lugarOrigen),
    comunaPublicId: row.comunaId,
    comunaNombre: comunaNombreById(pe, row.comunaId),
    barrioPublicId: row.barrioId,
    barrioNombre: barrioNombreById(pe, row.barrioId),
    fechaHora: row.fechaHora,
    latitud: row.latitud,
    longitud: row.longitud,
    direccion: row.direccion,
    responsablePublicId: row.responsableId || '',
    responsableNombre: row.responsableNombre || '',
    informePdf: row.informePdf,
    participantes: row.participantes.map((p) => mapWirePuntoParticipante(pe, catalog, p)),
  };
}

export function mapWireRedencionHeader(
  pe: OfflineSyncPuntosEntregaExportPort,
  header: RedencionBonoListItem,
): OfflineSyncWireRedencionHeader {
  return {
    id: header.id,
    publicId: header.publicId,
    entregaPublicId: header.entregaId,
    entregaNombre: header.entregaId ? entregaNombreById(pe, header.entregaId) : '',
    esRemanente: header.esRemanente,
    fecha: header.fecha,
    proveedorPublicId: header.proveedorId,
    proveedorNombre: header.proveedorNombre,
    responsablePublicId: header.responsableId || '',
    responsableNombre: header.responsableNombre || '',
    latitud: header.latitud ?? '',
    longitud: header.longitud ?? '',
    informePdf: '',
  };
}

export function mapWireRedencionParticipante(
  pe: OfflineSyncPuntosEntregaExportPort,
  catalog: RedencionBonoCatalogSnapshot,
  row: RedencionBonoParticipanteFormPayload,
): OfflineSyncWireRedencionParticipante {
  const ids = participanteCatalogWire(pe, row.participanteId);
  return {
    publicId: ids.publicId,
    documento: ids.documento,
    nombre: row.nombreParticipante,
    telefono: row.telefono,
    celular: row.celular,
    personaAutorizada: row.personaAutorizada,
    asistencia: row.asistencia,
    capacitado: row.capacitado,
    acta: row.acta,
    entregaBolsa: row.entregaBolsa,
    bonoPublicId: row.bonoId,
    bono: row.bono,
    tipoBonoPublicId: null,
    tipoBonoNombre: row.tipoBono.trim(),
    firmaBase64: row.firmaBase64,
    observaciones: row.observaciones,
    registroFotografico: row.registroFotografico,
    politicaTratamientos: row.politicaTratamientos,
  };
}

export function mapWireRedencionBundle(
  pe: OfflineSyncPuntosEntregaExportPort,
  catalog: RedencionBonoCatalogSnapshot,
  bundle: OfflineSyncRedencionBundle,
): OfflineSyncWireRedencionBundle {
  return {
    header: mapWireRedencionHeader(pe, bundle.header),
    participantes: bundle.participantes.map((p) => mapWireRedencionParticipante(pe, catalog, p)),
  };
}
